import React from "react";
import { useLocation } from "react-router";

import groupBy from "lodash/groupBy";
import { DateTime } from "luxon";
import useSWR from "swr";

import ListItem from "@mui/material/ListItem";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";

import { useFilter, useSort } from "@tzmedical/react-hooks";

import arrayIncludesAny from "../../../shared/arrayIncludesAny.js";
import { swrFetcher } from "../../axiosClient.js";
import Alert from "../../components/common/Alert.jsx";
import PageLoading from "../../components/common/PageLoading.jsx";
import EditButton from "../../components/EditButton.jsx";
import MetricForm from "../../components/MetricForm.jsx";
import PageContext from "../../contexts/PageContext.jsx";
import SearchContext from "../../contexts/SearchContext.jsx";
import useAuth0Roles from "../../hooks/auth0Roles.jsx";
import MetricGroup from "./MetricGroup.jsx";
import MetricsHeader from "./MetricsHeader.jsx";

//-----------------------------------------------------------------------------
// Page configuration
//-----------------------------------------------------------------------------
const DATA_REFRESH_INTERVAL_MS = 15000;
const searchFields = {
  person: "user",
  group: "group",
  metric: "name",
};
const sortOptions = {
  defaultSort: [
    {
      field: "group",
      reverse: false,
    },
    {
      field: "groupOrder",
      reverse: false,
    },
    {
      field: "user",
      reverse: false,
    },
    {
      field: "name",
      reverse: false,
    },
  ],
};

//-----------------------------------------------------------------------------
function ScorecardPage({
  // Props
  company,
  group,
}) {
  const location = useLocation();

  const { roles } = useAuth0Roles();
  const canEdit = React.useMemo(() => arrayIncludesAny(roles, ["Scorecard Admin"]), [roles]);

  const [weeks, setWeeks] = React.useState(13);
  const handlePeriodChange = React.useCallback((event) => {
    if (event.target.value === "month") {
      setWeeks(5);
    } else if (event.target.value === "year") {
      setWeeks(52);
    } else {
      setWeeks(13);
    }
  }, []);

  //---------------------------------------------------------------------------
  // Fetch the data using SWR
  //---------------------------------------------------------------------------
  const date = React.useMemo(() => DateTime.now().minus({ weeks }).toISODate(), [weeks]);
  const {
    data: swrData,
    error: swrError,
    isLoading: swrLoading,
    mutate,
  } = useSWR({ url: "/api/metrics", params: { company, group, date } }, swrFetcher, {
    refreshInterval: DATA_REFRESH_INTERVAL_MS,
  });

  const error = swrError ? swrError.response?.data?.error || swrError.message : null;
  const setError = React.useCallback(
    (val) => {
      if (val === null) {
        mutate();
      }
    },
    [mutate]
  );

  const metricsList = React.useMemo(() => swrData || [], [swrData]);
  const tableLoading = swrLoading && !metricsList.length;

  //---------------------------------------------------------------------------
  // Navbar button
  //---------------------------------------------------------------------------
  const { setPageButtons } = React.useContext(PageContext);
  React.useEffect(() => {
    setPageButtons(
      <span>
        <ListItem>
          <TextField
            label="Time Period"
            variant="outlined"
            fullWidth
            select
            defaultValue="quarter"
            onChange={handlePeriodChange}
          >
            <MenuItem value="quarter">Quarter</MenuItem>
            <MenuItem value="month">Month</MenuItem>
            <MenuItem value="year">Year</MenuItem>
          </TextField>
        </ListItem>
        {canEdit && (
          <>
            <ListItem>
              <MetricForm mutate={mutate} metricsList={metricsList} company={company}>
                Add Metric
              </MetricForm>
            </ListItem>
            <ListItem sx={{ mb: 1 }}>
              <EditButton data-cy="edit-data-button">Edit Data</EditButton>
            </ListItem>
          </>
        )}
      </span>
    );
    return () => setPageButtons(null);
  }, [setPageButtons, canEdit, metricsList, company, handlePeriodChange, location, mutate]);

  //---------------------------------------------------------------------------
  // Search
  //---------------------------------------------------------------------------
  const { search, setSearch } = React.useContext(SearchContext);
  const filteredMetrics = useFilter(metricsList, search, searchFields);
  const [sortedMetrics, handleSortSelection, sort] = useSort(filteredMetrics, sortOptions);
  React.useEffect(() => {
    return () => {
      // Reset the search bar every time we navigate to a new page
      setSearch("");
      mutate();
    };
  }, [setSearch, location, mutate]);

  //---------------------------------------------------------------------------
  //---------------------------------------------------------------------------
  const groupedMetrics = React.useMemo(() => groupBy(sortedMetrics, "group"), [sortedMetrics]);

  if (tableLoading && !metricsList.length) {
    return (
      <>
        <Alert message={error} setMessage={setError} level="error" />
        {!error && <PageLoading />}
      </>
    );
  }

  //---------------------------------------------------------------------------
  // Render
  //---------------------------------------------------------------------------
  return (
    <>
      <Alert message={error} setMessage={setError} level="error" />
      <MetricsHeader sort={sort} setSort={handleSortSelection} />
      {Object.keys(groupedMetrics).map((key) => (
        <React.Fragment key={key}>
          <MetricGroup
            name={key}
            metrics={groupedMetrics[key]}
            metricsList={metricsList}
            weeks={weeks}
            mutate={mutate}
          />
        </React.Fragment>
      ))}
    </>
  );
}

export default ScorecardPage;
